import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import "./backoffice.css";
import { getSecteurs } from "../../../redux/actions/promoteursActions";
import PermissionsSwitchButton from "./PermissionsSwitchBtn";
import {
  createAccount,
  getUsersList,
  updateUser,
} from "../../../redux/actions/usersListAction";
import toast from "react-hot-toast";
import "./editCollaboratorModal.css";
import { imageLoader, uploadImage } from "../../../utils/imageLoader";
import { closeModal, openModal } from "../../../redux/actions/modalActions";
import mainAxios from "../../../redux/api/mainAxios";
import ConfirmationModal from "./ConfirmationModal";

const EditCollaboratorModal = ({ documentId, isCreating = false }) => {
  const dispatch = useDispatch();
  const [imagePreview, setImagePreview] = useState(null);
  const { usersList } = useSelector((state) => state.userListReducer);
  const [isLoading, setIsLoading] = useState(false);
  const currentUser = useSelector((state) => state.userAuthReducer.user);

  const [formData, setFormData] = useState({
    name: "",
    profile: "Collaborateur",
    phone_number: "",
    email: "",
    password: "",
    profile_image: null,
    honoraires: "",
    secteurs: [],
    assignSecteur: false,
    createPromoter: false,
    createSecteur: false,
    updateCatalogue: false,
    responsableProgramme: false,
    canHideShowProgramme: false,
  });

  // Profile options
  const profileOptions = ["Collaborateur", "Administrateur"];
  const [showSecteurDropdown, setShowSecteurDropdown] = useState(false);
  const dropdownRef = useRef(null);
  const { secteurs } = useSelector((state) => state.promoteursReducer);

  useEffect(() => {
    if (!isCreating && documentId) {
      const user = usersList.find((user) => user.documentId === documentId);

      if (user) {
        setFormData({
          id: user.id,
          name: user.name || "",
          profile: user.profile || "Collaborateur",
          profile_image: user.profile_image || null,
          phone_number: user.phone_number || "",
          email: user.email || "",
          password: "",
          honoraires: user.honoraires || "",
          secteurs: user.secteurs || [],
          assignSecteur: user.assignSecteur || false,
          createPromoter: user.createPromoter || false,
          createSecteur: user.createSecteur || false,
          updateCatalogue: user.updateCatalogue || false,
          responsableProgramme: user.responsableProgramme || false,
          canHideShowProgramme: user.canHideShowProgramme || false,
        });
        if (user.profile_image?.url) {
          setImagePreview(imageLoader(user.profile_image.url));
        }
      }
    }
  }, [usersList, isCreating, documentId]);

  useEffect(() => {
    dispatch(getSecteurs());
  }, [dispatch]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowSecteurDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const newFormData = {
        ...prev,
        [name]: value,
      };

      // Clear secteurs when responsableProgramme is disabled
      if (name === "responsableProgramme" && !value) {
        newFormData.secteurs = [];
      }

      return newFormData;
    });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const previewUrl = URL.createObjectURL(file);
      setImagePreview(previewUrl);
      setFormData((prev) => ({
        ...prev,
        profile_image: {
          id: prev.profile_image?.id,
          url: file,
        },
      }));
    }
  };

  // Handler for delete button click
  const handleDeleteClick = () => {
    dispatch(
      openModal({
        title: "Confirmation",
        content: (
          <ConfirmationModal
            onConfirm={handleConfirmDelete}
            onCancel={() => dispatch(closeModal())}
          />
        ),
      }),
    );
  };

  // Handler for confirmed deletion
  const handleConfirmDelete = async () => {
    try {
      const user = usersList.find((user) => user.documentId === documentId);

      await mainAxios.delete(`/api/users/${user.id}`);
      toast.success("Collaborateur supprimé avec succès");
      dispatch(getUsersList());
      dispatch(closeModal());
    } catch (error) {
      console.error("Failed to delete user:", error);
      dispatch(closeModal());
      toast.error(
        "Échec de la suppression du collaborateur. Veuillez réessayer.",
      );
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    // Validate required fields
    if (!formData.name || !formData.email) {
      toast.error("Le nom et l'email sont obligatoires");
      setIsLoading(false);
      return;
    }

    // Validate password for user creation or if a new password is provided
    if ((isCreating || formData.password) && formData.password.length < 6) {
      toast.error("Le mot de passe doit contenir au moins 6 caractères");
      setIsLoading(false);
      return;
    }

    try {
      // Create a clean copy of the form data
      let updatedFormData = { ...formData };

      // Remove password field for updates if it's empty
      if (!isCreating && !updatedFormData.password) {
        delete updatedFormData.password;
      }

      // Handle image upload if there's a new image
      if (formData.profile_image?.url instanceof File) {
        const uploadedImageUrl = await uploadImage(formData.profile_image.url);

        updatedFormData = {
          ...updatedFormData,
          profile_image: uploadedImageUrl,
        };
      }

      if (isCreating) {
        // Create new user
        await dispatch(
          createAccount({
            email: formData.email,
            password: formData.password,
            name: formData.name,
            profile: formData.profile,
            phone_number: formData.phone_number,
            honoraires: formData.honoraires,
            assignSecteur: formData.assignSecteur,
            createPromoter: formData.createPromoter,
            createSecteur: formData.createSecteur,
            updateCatalogue: formData.updateCatalogue,
            canHideShowProgramme: formData.canHideShowProgramme,
            responsableProgramme: formData.responsableProgramme,
            profile_image: updatedFormData.profile_image || null,
            secteurs: formData.secteurs || [], // Include the selected sectors
          }),
        );
        toast.success("Utilisateur créé avec succès");
      } else {
        // Make sure we have the user ID for update
        if (!updatedFormData.id) {
          throw new Error("User ID is required for update");
        }

        // Update existing user
        await dispatch(updateUser(updatedFormData));
      }

      // Refresh user list
      dispatch(getUsersList());

      // Close the modal after successful operation
      dispatch(closeModal());
    } catch (error) {
      console.error("Error:", error);
      toast.error(
        isCreating
          ? "Erreur lors de la création de l'utilisateur"
          : "Erreur lors de la mise à jour de l'utilisateur",
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="edit-collaborator-form d-flex flex-column gap20"
      style={{ width: "80vw" }}
    >
      {/* Profile Image */}
      <div className="form-group d-flex flex-column mx-auto text-center position-relative">
        <img
          src={imagePreview || imageLoader(formData.profile_image?.url)}
          alt="pic"
          height={200}
          width={200}
          className="rounded-circle"
          style={{ objectFit: "contain " }}
        />
        <input
          type="file"
          accept="image/*"
          onChange={handleImageChange}
          title={isCreating ? "Ajouter une image" : "Changer l'image"}
          className="form-control profile_image_input"
        />
      </div>
      {/* Name Input */}
      <div className="form-group">
        <label>Nom</label>
        <input
          type="text"
          name="name"
          value={formData.name}
          onChange={handleInputChange}
          className="form-control"
          required
        />
      </div>

      {/* Email Input */}
      <div className="form-group">
        <label>Email</label>
        <input
          type="email"
          name="email"
          value={formData.email}
          onChange={handleInputChange}
          className="form-control"
          required
        />
      </div>

      {/* Password Input - Always shown but only required in creation mode */}
      <div className="form-group">
        <label>
          {isCreating ? "Mot de passe" : "Changer le mot de passe"}
          {!isCreating && (
            <span className="text-muted small">
              {" "}
              (laisser vide pour ne pas modifier)
            </span>
          )}
        </label>
        <input
          type="password"
          name="password"
          value={formData.password}
          onChange={handleInputChange}
          className="form-control"
          placeholder={
            isCreating
              ? "Minimum 6 caractères"
              : "Nouveau mot de passe (minimum 6 caractères)"
          }
          required={isCreating}
        />
      </div>

      {/* Secteur Dropdown */}
      {currentUser && currentUser.profile === "Administrateur" && (
        <div className="form-group">
          <label>Secteurs</label>
          <div className="custom-dropdown" ref={dropdownRef}>
            <div
              className={`form-control d-flex align-items-center justify-content-between dropdown-toggle-custom ${
                !formData.responsableProgramme ? "disabled" : ""
              }`}
              onClick={() =>
                formData.responsableProgramme &&
                setShowSecteurDropdown(!showSecteurDropdown)
              }
              style={{
                opacity: formData.responsableProgramme ? 1 : 0.5,
                cursor: formData.responsableProgramme
                  ? "pointer"
                  : "not-allowed",
              }}
            >
              <div className="selected-items">
                {formData.secteurs?.length
                  ? formData.secteurs.map((s) => s.name).join(", ")
                  : "Sélectionner les secteurs"}
              </div>
              <div className="dropdown-arrow">
                <i
                  className={`fas fa-chevron-${
                    showSecteurDropdown ? "up" : "down"
                  }`}
                ></i>
              </div>
            </div>

            {showSecteurDropdown && (
              <div className="dropdown-options">
                {/* Select All Option */}
                <div
                  className="dropdown-item d-flex align-items-center gap10"
                  onClick={(e) => {
                    e.stopPropagation();
                    const allSelected =
                      formData.secteurs?.length === secteurs.data?.length;

                    const newSecteurs = allSelected
                      ? [] // Deselect all
                      : [...(secteurs.data || [])]; // Select all

                    handleInputChange({
                      target: { name: "secteurs", value: newSecteurs },
                    });
                  }}
                  style={{
                    borderBottom: "1px solid #eee",
                    marginBottom: "5px",
                    paddingBottom: "5px",
                  }}
                >
                  <div
                    className={`checkbox ${
                      formData.secteurs?.length === secteurs.data?.length
                        ? "checked"
                        : ""
                    }`}
                  >
                    {formData.secteurs?.length === secteurs.data?.length && (
                      <i className="fas fa-check"></i>
                    )}
                  </div>
                  <span style={{ fontWeight: "bold" }}>Sélectionner tout</span>
                </div>

                {secteurs.data?.map((secteur) => (
                  <div
                    key={secteur.documentId}
                    className="dropdown-item d-flex align-items-center gap10"
                    onClick={(e) => {
                      e.stopPropagation();
                      const isSelected = formData.secteurs?.some(
                        (s) => s.documentId === secteur.documentId,
                      );

                      const newSecteurs = isSelected
                        ? formData.secteurs.filter(
                            (s) => s.documentId !== secteur.documentId,
                          )
                        : [...(formData.secteurs || []), secteur];

                      handleInputChange({
                        target: { name: "secteurs", value: newSecteurs },
                      });
                    }}
                  >
                    <div
                      className={`checkbox ${
                        formData.secteurs?.some(
                          (s) => s.documentId === secteur.documentId,
                        )
                          ? "checked"
                          : ""
                      }`}
                    >
                      {formData.secteurs?.some(
                        (s) => s.documentId === secteur.documentId,
                      ) && <i className="fas fa-check"></i>}
                    </div>
                    <span>{secteur.name}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
      {/* Phone Input */}
      <div className="form-group">
        <label>Téléphone</label>
        <input
          type="tel"
          name="phone_number"
          value={formData.phone_number}
          onChange={handleInputChange}
          className="form-control"
        />
      </div>
      {/* Profile Dropdown */}
      {currentUser && currentUser.profile === "Administrateur" && (
        <div className="form-group">
          <label>Profil</label>
          <select
            name="profile"
            value={formData.profile}
            onChange={handleInputChange}
            className="form-control"
            required
          >
            {profileOptions.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>
      )}
      {/* Honoraires Input */}
      <div className="form-group">
        <label>Honoraires (%)</label>
        <input
          type="number"
          name="honoraires"
          value={formData.honoraires || 0}
          onChange={handleInputChange}
          className="form-control"
          min="0"
          max="100"
        />
      </div>
      {/* Permissions Section */}
      {currentUser && currentUser.profile === "Administrateur" && (
        <div className="permissions-section mb-20">
          <label className="mb-2">Permissions</label>
          <hr />
          <div className="d-flex flex-wrap gap-4 justify-content-between">
            <div className="w30">
              <PermissionsSwitchButton
                label="Masquer/afficher programme"
                isActive={formData.canHideShowProgramme}
                onToggle={(newValue) =>
                  handleInputChange({
                    target: { name: "canHideShowProgramme", value: newValue },
                  })
                }
              />
            </div>
            <div className="w30">
              <PermissionsSwitchButton
                label="Assigner secteur"
                isActive={formData.assignSecteur}
                onToggle={(newValue) =>
                  handleInputChange({
                    target: { name: "assignSecteur", value: newValue },
                  })
                }
              />
            </div>
            <div className="w30">
              <PermissionsSwitchButton
                label="Créer secteur"
                isActive={formData.createSecteur}
                onToggle={(newValue) =>
                  handleInputChange({
                    target: { name: "createSecteur", value: newValue },
                  })
                }
              />
            </div>
            <div className="w30">
              <PermissionsSwitchButton
                label="Responsable du programme"
                isActive={formData.responsableProgramme}
                onToggle={(newValue) =>
                  handleInputChange({
                    target: { name: "responsableProgramme", value: newValue },
                  })
                }
              />
            </div>
            <div className="w30">
              <PermissionsSwitchButton
                label="Créer/Modifier promoteur"
                isActive={formData.createPromoter}
                onToggle={(newValue) =>
                  handleInputChange({
                    target: { name: "createPromoter", value: newValue },
                  })
                }
              />
            </div>
            <div className="w30">
              <PermissionsSwitchButton
                label="Mettre à jour catalogue"
                isActive={formData.updateCatalogue}
                onToggle={(newValue) =>
                  handleInputChange({
                    target: { name: "updateCatalogue", value: newValue },
                  })
                }
              />
            </div>
          </div>
        </div>
      )}
      {/* Submit Button */}
      <div className="form-actions d-flex gap10 justify-content-between align-items-center">
        {/* Only show delete button when editing an existing user AND it's not the current logged-in user */}
        {!isCreating && currentUser && formData.id !== currentUser.id && (
          <button
            type="button"
            className="border-0 bg-danger radius5 px-20 py-10 white hoverOpacity"
            onClick={handleDeleteClick}
          >
            Supprimer
          </button>
        )}
        <button
          type="submit"
          className={`border-0 bg-pink radius5 px-20 py-10 white hoverOpacity ${
            isLoading ? "opacity-50 bg-soft-grey" : ""
          }`}
          disabled={isLoading}
        >
          {isLoading
            ? isCreating
              ? "Création..."
              : "Enregistrement..."
            : isCreating
              ? "Créer l'utilisateur"
              : "Enregistrer les modifications"}
        </button>
      </div>
    </form>
  );
};

export default EditCollaboratorModal;
